const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const getFormatter = (opts?: Intl.NumberFormatOptions) => {
  if (opts)
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...opts,
    });
  return formatter;
};

export const formatAmount = (num: number, opts?: Intl.NumberFormatOptions) => {
  return getFormatter(opts).format(num);
};

export const formatPercent = (num: number, opts?: Intl.NumberFormatOptions) => {
  return new Intl.NumberFormat("en-US", {
    ...opts,
    style: "percent",
  }).format(num);
};

export const formatNumber = (num: number, opts?: Intl.NumberFormatOptions) => {
  return new Intl.NumberFormat("en-US", {
    ...opts,
  }).format(num);
};

const RTF = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
const RELATIVE_DATE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 60 * 60],
  ["month", 30 * 24 * 60 * 60],
  ["week", 7 * 24 * 60 * 60],
  ["day", 24 * 60 * 60],
];

/**
 * Formats an ISO date string as relative time (e.g. "in 3 months", "2 days ago").
 * Handles both past and future dates.
 */
export const formatRelativeDate = (isoString: string): string => {
  const diffSeconds = (new Date(isoString).getTime() - Date.now()) / 1000;
  for (const [unit, seconds] of RELATIVE_DATE_UNITS) {
    if (Math.abs(diffSeconds) >= seconds) {
      return RTF.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return RTF.format(Math.round(diffSeconds / (24 * 60 * 60)), "day");
};

/** Formats an ISO date string as an absolute short date (e.g. "Jun 21, 2026"). */
export const formatShortDate = (isoString: string): string =>
  new Date(isoString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diff = Math.floor((now.getTime() - new Date(date).getTime()) / 1000);

  if (diff < 60) return "just now";
  if (diff < 3_600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.floor(diff / 3_600)}h ago`;
  if (diff < 2_592_000) return `${Math.floor(diff / 86_400)}d ago`;
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
};

/** Formats a byte count as a human-readable file size (e.g. "2.4 MB"). */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatReadingTime = (text: string): string => {
  const words = text.split(" ").length;
  const minutes = Math.ceil(words / 180);
  return `${minutes} min read`;
};
